<i18n
  src="@/locales/views/agro_exporter/certifications/providers/list_providers.yml"
  lang="yaml"
></i18n>
<template>
  <div class="mt-8 mb-8">
    <h1 class="page-title">{{ module_name }}</h1>
  </div>

  <template v-if="load_data_general">
    <div class="flex flex-row">
      <div class="panel-plain-label">
        <badge-default
          :label="
            total_number_providers + ' ' + $t('labels.total_number_providers')
          "
        />
      </div>
    </div>

    <div class="mt-8 flex flex-col">
      <ag-grid-vue
        style="width: 100%; height: 650px"
        class="ag-theme-alpine w-full h-full"
        :columnDefs="columnDefs"
        @grid-ready="onGridReady"
        :defaultColDef="defaultColDef"
        :rowData="columnsData"
      >
      </ag-grid-vue>
    </div>
  </template>

  <loading-message v-else />
</template>

<script>
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { AgGridVue } from "ag-grid-vue3";
import GoToLink from "@/components/views/layout/ag_grid/GoToLink.vue";

import Requester from "@/views/agro_exporter/requesters/certifications/providers/list_providers.js";
export default {
  name: "AECertificationsPartnersListProviders",
  components: { AgGridVue },
  data() {
    return {
      load_data_general: false,
      certifications: [],
      organizations: [],
      packing_houses: [],
      producers: [],
      defaultColDef: {
        sortable: true,
        filter: true,
        floatingFilter: true
      },
      columnDefs: []
    };
  },
  props: { agro_exporter_id: { required: true } },
  created() {
    this.checkIfAuthenticated().then(() =>
      this.checkIfAuthorized({
        role: this.$route.meta.role,
        permission: this.$route.meta.permission,
        entity: {
          agro_exporter_id: this.agro_exporter_id
        }
      }).then(() => this.loadData())
    );
  },
  computed: {
    total_number_providers() {
      return this.organizations.length + this.packing_houses.length;
    },
    columnsData() {
      let columns_data = [];
      columns_data.push(
        ...this.organizations.map(organization => {
          let org_certifications = {};
          this.certifications.forEach(certification => {
            org_certifications[`${certification.id}`] = this.certificationLabel(
              organization.organization_certifications,
              certification.id
            );
          });
          return {
            id: organization.id,
            name: organization.name,
            entity: organization.entity,
            ...org_certifications,
            route: this.routeName("show_organization"),
            route_params: {
              agro_exporter_id: this.agro_exporter_id,
              organization_id: organization.id
            }
          };
        })
      );

      columns_data.push(
        ...this.producers.map(producer => {
          let crop_field_certifications = {};
          this.certifications.forEach(certification => {
            crop_field_certifications[`${certification.id}`] = this.certificationLabel(
              producer.crop_fields_certifications,
              certification.id
            );
          });
          return {
            id: producer.id,
            name: producer.name,
            entity: producer.entity,
            ...crop_field_certifications,
            route: this.routeName("show_producer"),
            route_params: {
              agro_exporter_id: this.agro_exporter_id,
              producer_id: producer.id
            }
          };
        })
      );

      columns_data.push(
        ...this.packing_houses.map(packing_house => {
          let ph_certifications = {};
          this.certifications.forEach(certification => {
            ph_certifications[`${certification.id}`] = this.certificationLabel(
              packing_house.packing_house_certifications,
              certification.id
            );
          });
          return {
            id: packing_house.id,
            name: packing_house.name,
            entity: packing_house.entity,
            ...ph_certifications,
            route: this.routeName("show_packing_house"),
            route_params: {
              agro_exporter_id: this.agro_exporter_id,
              packing_house_id: packing_house.id
            }
          };
        })
      );

      return columns_data;
    }
  },
  methods: {
    loadData: async function () {
      await this.fetchCertifications();
      await this.fetchOrganizations();
      await this.fetchPackingHouses();
      await this.fetchProducers();
      this.fillColumnsDef();
      this.load_data_general = true;
    },
    fetchCertifications: async function () {
      let response = await Requester.fetchCertifications(this.agro_exporter_id);

      this.certifications = response.data;
    },
    fetchPackingHouses: async function () {
      let response = await Requester.fetchPackingHouses(this.agro_exporter_id);
      this.packing_houses = response.data;
      this.packing_houses.forEach(packing_house => {
        packing_house['entity'] = 'Planta de empaque'
      })
    },
    fetchOrganizations: async function () {
      let response = await Requester.fetchOrganizations(this.agro_exporter_id);
      this.organizations = response.data;
      this.organizations.forEach(organization => {
        organization['entity'] = 'Organización'
      })
    },
    fetchProducers: async function() {
      let response = await Requester.fetchProducers(this.agro_exporter_id);
      this.producers= response.data;
      this.producers.forEach(producer =>{
        producer['entity'] = 'Productor'
      })
    },
    certificationLabel(provider_certifications, certification_id) {
      let provider_certification = provider_certifications.find(
        provider_cert => provider_cert.certification_id == certification_id
      );

      if (provider_certification) {
        let days = this.daysUntilExpirationDate(
          provider_certification.expiration_date
        );

        if (days < 0) {
          return `
            <span class="text-red-500">
            <i class="fa-solid fa-triangle-exclamation text-red-500"></i>
            ${this.$t("labels.expired_date")}
            </span>`;
        } else {
          return `<span>
            <i class="fa-solid fa-circle-check text-green-500"></i>
            ${this.$t("labels.days", { days: this.numberWithPrecision(days) })}
            </span>`;
        }
      }
      return "<span/>";
    },
    fillColumnsDef() {
      this.columnDefs = [
        {
          headerName: this.$t("labels.provider"),
          field: "entity",
          filter: true,
          resizable: true
        },
        {
          headerName: this.$t("labels.provider"),
          field: "name",
          filter: true,
          resizable: true,
          width: 400,
          cellRendererSelector: params => {
            return {
              component: GoToLink,
              params: {
                text: params.value,
                path_name: params.data.route,
                params_path: params.data.route_params
              }
            };
          }
        },
        ...this.certifications.map(cert => {
          return {
            headerName: cert.name,
            field: `${cert.id}`,
            filter: true,
            cellRendererSelector: params => {
              const LabelCertComponent = {
                template: params.value
              };
              return {
                component: LabelCertComponent
              };
            }
          };
        })
      ];
    },
    goToShowOrganization(organization_id) {
      this.$router.push({
        name: this.routeName("show_organization"),
        params: {
          agro_exporter_id: this.agro_exporter_id,
          organization_id: organization_id
        }
      });
    },
    goToShowPackingHouse(packing_house_id) {
      this.$router.push({
        name: this.routeName("show_packing_house"),
        params: {
          agro_exporter_id: this.agro_exporter_id,
          packing_house_id: packing_house_id
        }
      });
    },
    sizeToFit() {
      this.gridApi.sizeColumnsToFit({
        defaultMinWidth: 250
      });
    },
    onGridReady(params) {
      this.gridApi = params.api;
      this.gridColumnApi = params.columnApi;
      this.sizeToFit();
    }
  }
};
</script>
